import { Fragment, useState, useEffect } from 'react'
import { IonPage, useIonModal, useIonToast } from '@ionic/react'
import Toast from '../utils/Toast'
import { useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'
import ListItemButton from '@mui/material/ListItemButton'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import SyncIcon from '@mui/icons-material/Sync'
import IconButton from '@mui/material/IconButton'
import { useAuth } from '../auth'
import SearchField from '../components/SearchField'
import GoogleMap from '../components/GoogleMap'
import { useTowers } from '../data/tower'
import { HOME_PATH, SETTINGS_UPGRADE_PLAN_PATH } from '../path'
import { get_pin_color } from '../utils/googleMap'
import UpgradePopup from '../components/UpgradePopup'
import QuotaExceededModal from '../components/QuotaExceededModal'
import TeamToggledModal from '../components/TeamToggledModal'
import { useLogFeatureInfo } from '../utils/session'
import localStorage from '../utils/localStorage'
import { useToggleTowerForecast } from '../data/hooks/useToggleTowerForecast'

const TOWER_HEIGHT = 230
const TOWER_HEIGHT_PX = `${TOWER_HEIGHT}px`
const ADVANCED_FILTER_STORAGE_KEY = 'search_advanced_forecast_only'

const FilterModal = styled.div`
	position: fixed;
	top: 0;
	right: 0;
	width: 100%;
	height: 100%;
	background: white;
	z-index: 9999;
	transform: translateX(100%);
	transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

	&.open {
		transform: translateX(0);
	}

	& .modal-header {
		display: flex;
		align-items: center;
		padding: 16px;
		border-bottom: 1px solid #e0e0e0;
		background-color: #f8f8f8;

		& .close-button {
			margin-right: 12px;
		}

		& .modal-title {
			font-size: 18px;
			font-weight: 600;
			color: #1a1a1a;
		}
	}

	& .modal-content {
		padding: 24px 16px;

		& .filter-section {
			margin-bottom: 24px;

			& .section-title {
				font-size: 16px;
				font-weight: 600;
				color: #1a1a1a;
				margin-bottom: 12px;
			}

			& .filter-option {
				padding: 8px 0;

				& .filter-label {
					font-size: 14px;
					font-weight: 500;
					color: #444;
				}
			}
		}
	}

	@media (min-width: 769px) {
		display: none;
	}
`

const Container = styled.div`
	display: grid;
	grid-template-areas: 'list map';
	grid-template-columns: 380px 1fr;
	grid-template-rows: 100vh;
	grid-template-rows: -webkit-fill-available;

	& .tower-list-mobile {
		display:none;
	}

	& .metaText {
		z-index: 130; 
		position: absolute;
		left: env(keyboard-inset-height, 15px);
	}

	& .tower-item {
		display: grid;
		grid-template-areas:
			'name 		label'
			'details	switch';
		grid-template-columns: 1fr 50px;
		width: 100%;
		padding: 16px;
		flex-direction: column;
		justify-content: flex-start;
		align-items: flex-start;
		
		&.disabled {
			& .tower-name, .tower-map-label, .tower-details {
				opacity:0.5;
			}
			
			& .tower-map-label div {
				background-color: #919191;
				border: 1px solid #515151;
			}
		}

		& .toggle {
			grid-area: switch;
			display: flex;
			align-items: end;
			justify-content: center;
			height: 100%;
			margin-top: 7px;
		}

		& .tower-name {
			grid-area: name;
			padding: 0px 0px 2px 8px;
			
			display: flex;

			& .t-name  {
				font-weight: bold;	
			}

			& .with-24h {
				border: 1px solid #00a5ff;
				width: 54px;
				height: 20px;
				border-radius: 20px;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 14px;
				font-weight: 500;
				color: white;
				background-color: #00a5ff;
				margin-top: 2px;
				margin-left: 7px;
			}
		}

		& .tower-map-label {
			grid-area: label;
			display: flex;
			justify-content: center;

			& div {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 36px;
				height: 36px;
				background-color: var(--pin-color, #e04633);
				border: 1px solid var(--pin-border-color, #B8372C);
				border-radius: 34px;
				color: white;
			}
		}

		& .tower-details {
			grid-area: details;
		}

		& .tower-detail {
			padding-left: 8px;
		}

		& .tower-default {
			margin-left: 8px;
			margin-top: 16px;
		}
	}

	& .tower-list-desktop {
		grid-area: list;
		box-shadow: rgb(0 0 0 / 35%) 0px 8px 15px;
		z-index: 1;
		border-right: 1px solid rgba(0, 0, 0, 0.12);

		& .search-input {
			padding: 8px 8px 16px 8px;

			& .advanced-filter-toggle {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 4px;
				cursor: pointer;
				padding: 8px;
				margin-top: 8px;
				color: var(--mu-primary-dark, #1767c1);
				font-size: 14px;
				font-weight: 500;
				transition: all 0.2s ease;

				&:hover {
					background-color: #f5f5f5;
					border-radius: 6px;
				}

				& svg {
					font-size: 20px;
				}
			}

			& .advanced-filters {
				margin-top: 12px;
				padding: 12px;
				background-color: #f8f8f8;
				border-radius: 8px;
				border: 1px solid #e0e0e0;

				& .filter-label {
					font-size: 13px;
					font-weight: 500;
					color: #444;
				}
			}

			& .legend {
				display: grid;
				grid-template-columns: 1fr 1fr;
				grid-template-rows: 28px 24px;
				margin-top: 18px;

				& .legend-item {
					display: flex;
					justify-content: center;

					& .legend-container {
						width: 140px;
						display: flex;
						justify-content: start;
					}
				}

				& .swatch {
					width: 18px;
					min-width: 18px;
					height: 18px;
					border-radius: 18px;
					background-color: var(--pin-color,#e04633);
					border: 1px solid var(--pin-border-color,#B8372C);
					margin-right: 10px;
				}

				& .swatch-label {
					white-space: nowrap;
					-webkit-user-select: none; /* Safari */
					-ms-user-select: none; /* IE 10 and IE 11 */
					user-select: none; /* Standard syntax */
				}
			}
		}

		& .towers-loading {
			display: flex;
			align-items: center;
			justify-content: center;
			padding-top: 30px;
		}

		& .tower-items {
			overflow-y: scroll;
			height: 100vh;
		}

	}

	& .googlep-map {
		grid-area: map;	
	}

	& .fab-btn {
		position:absolute;
		bottom:16px; 
		right: unset;
		left:16px;
	}

	@media (max-width: 768px) {
		grid-template-areas: 'map';
		grid-template-columns: 1fr;

		& .tower-list-desktop {
			display: none;
		}

		& .tower-list-mobile {
			display: block;
			position: absolute;
			width: 100%;
			height: calc(100% - 215px);

			&.short-cards {
				height: calc(100% - 175px);				
			}

			& .search-input {
				position: relative;
				padding: 12px;
				z-index: 20;

				& .legend {
					display: grid;
					grid-template-columns: 1fr 1fr;
					grid-template-rows: 28px 24px;
					margin-top: 18px;
					background-color: #0000006e;
					height: 75px;
					align-items: center;
					padding: 10px;
					border-radius: 20px;
					backdrop-filter: blur(3px);

					& .legend-item {
						display: flex;
						display: flex;
						justify-content: center;

						& .legend-container {
							width: 140px;
							display: flex;
							justify-content: start;
						}
					}

					& .swatch {
						width: 18px;
						min-width: 18px;
						height: 18px;
						border-radius: 18px;
						background-color: var(--pin-color,#e04633);
						border: 1px solid var(--pin-border-color,#B8372C);
						margin-right: 10px;
					}

					& .swatch-label {
						white-space: nowrap;
						color: white;
						-webkit-user-select: none; /* Safari */
						-ms-user-select: none; /* IE 10 and IE 11 */
						user-select: none; /* Standard syntax */
					}
				}
			}

			& .tower-items {
				overflow-x: scroll;
				width: 100%;
				position: fixed;
				bottom: calc(0px + var(--ios-keyboard-offset, 0px));
				transition: bottom 200ms ease-out;
				z-index: 20;
			}

			& .tower-left-gutter {
				margin-left: 8px;
			}

			& .tower-card {
				cursor: pointer;
				min-width: calc(100vw - 24px);
				width: calc(100vw - 24px);
				height: 185px;
				background-color: white;
				margin: 3px;
				border-radius: 12px;
				border: 1px solid #0000001a;
				box-shadow: rgb(0 0 0 / 35%) 0px 8px 15px;

				&:hover {
					background-color: #e7e7e7;
				}

				&.short-card {
					height: 145px;
				}
			}
		}

		& .fab-btn {
			bottom: 55px;
			right: 31px;
			left: unset;
		}
	}
`

/**
 * Filters towers
 *
 * @param	{[Tower]}	towers[]
 * @param	{Object}		searchCriteria
 * @param	{String}			.text
 * @param	{Boolean}		.exact
 * @param	{Boolean}		.advanced_forecast_only
 *
 * @return {[Tower]}	filteredTowers
 */
const filterTowers = (towers:any, searchCriteria:any) => {
	const text = (searchCriteria?.text||'').toLowerCase().trim()
	const exact = searchCriteria?.exact
	const advanced_forecast_only = searchCriteria?.advanced_forecast_only

	let filtered_towers = towers || []

	// Filter by text search
	if (text) {
		const filterFn = exact
			? (name:string, serial:string) => name == text || serial == text
			: (name:string, serial:string) => name.indexOf(text) >= 0 || serial.indexOf(text) >= 0

		filtered_towers = filtered_towers.filter((t:any) => {
			const towerName = (t?.name||'').toLowerCase().trim()
			const serial = (t?.serial_no||'').toLowerCase().trim()
			return filterFn(towerName, serial)
		})
	}

	// Filter by 24h forecast access (using new twenty_four_hrs_forecast_toggled.value)
	if (advanced_forecast_only) {
		filtered_towers = filtered_towers.filter((t:any) => t?.twenty_four_hrs_forecast_toggled?.value === true)
	}

	return filtered_towers
}

const get_tower_status = (latest_reading?:any, online?:any) => {
	if (!online)
		return 'offline'
	else if (latest_reading?.hazard !== null)
		return latest_reading?.hazardous_status
	else
		return 'absent'
}

const TowerDetails = ({ ...props }) => {
	const { tower, on_click, show_toggle_switch, get_tower_toggle_info } = props
	const color = get_pin_color(get_tower_status(tower?.latest_reading, tower?.online))
	const dark_color = get_pin_color(get_tower_status(tower?.latest_reading, tower?.online), true)
	const [reset_counter, set_reset_counter] = useState(0)

	// Use the new twenty_four_hrs_forecast_toggled.value for toggle state
	const toggle_info = get_tower_toggle_info ? get_tower_toggle_info(tower?.id) : { is_toggled: false, is_team_toggled: false, is_pending: false }
	const [checked, set_checked] = useState(toggle_info.is_toggled)

	// Update checked state when toggle info changes
	useEffect(() => {
		if (get_tower_toggle_info) {
			const info = get_tower_toggle_info(tower?.id)
			set_checked(info.is_toggled)
		}
	}, [tower?.twenty_four_hrs_forecast_toggled?.value, tower?.id, get_tower_toggle_info, toggle_info.is_toggled])

	const color_style = {
		'--pin-color': color,
		'--pin-border-color': dark_color
	} as React.CSSProperties

	const root_classes = ['tower-item']
	if (tower.disabled)
		root_classes.push('disabled')

	const reset = () => set_reset_counter(reset_counter+1)

	const select_tower = () => {
		if (tower.disabled && props.show_toggle_towers)
			props.show_toggle_towers(reset)
		else
			on_click()
	}

	const toggle_tower = (toggle:any) => {
		if (props.toggle_tower)
			props.toggle_tower(tower.id, toggle, set_checked)
	}

	// Prevent toggle area clicks from selecting tower
	const stop_propagation = (e:any) => {
		e.stopPropagation()
	}

	// Determine badge style based on state
	const get_badge_style = () => {
		if (toggle_info.is_team_toggled) {
			return { backgroundColor: '#6b7280', borderColor: '#6b7280' }
		}
		return {}
	}

	const get_badge_text = () => {
		if (toggle_info.is_team_toggled) return 'Team'
		return '24H'
	}

	return (<div className={root_classes.join(' ')} onClick={select_tower}>
		<Typography component="div" variant="body1" className="tower-name">
			<div className="t-name">{tower?.name}</div>{!toggle_info.is_toggled ? null :
			<div className="with-24h" style={get_badge_style()}>
				{get_badge_text()}
			</div>}
		</Typography>
		<div className="tower-map-label" style={color_style}>
			<div><Typography variant="body2">{tower?.map_label}</Typography></div>
		</div>
		<div className="tower-details">
			{tower?.distance !== null && <Typography variant="body2" color="text.secondary" className="tower-detail">{`Distance · ${tower?.distance.toFixed(2)} km`}</Typography>}
			{tower?.direction && <Typography variant="body2" color="text.secondary" className="tower-detail">{`Direction · ${tower?.direction}`}</Typography>}
			{tower?.coord && <Typography variant="body2" color="text.secondary" className="tower-detail">{`Lat/Lon · ${tower?.coord.lat} ${tower?.coord.long}`}</Typography>}
			{tower?.serial_no && <Typography variant="body2" color="text.secondary" className="tower-detail">{`Serial · ${tower?.serial_with_id}`}</Typography>}
			{tower.isDefault && <Chip label="Default" variant="outlined" color="primary" size="small" className="tower-default"/>}
		</div>
		<div className="toggle" onClick={show_toggle_switch ? stop_propagation : undefined}>{!show_toggle_switch ? null :
			<FormControlLabel
				value="start"
				control={<Switch
					color="primary"
					checked={checked}
					onChange={(e:any) => toggle_tower(e.target.checked)}
					/>}
				label={<Typography variant="body2" color="primary">24H</Typography>}
				labelPlacement="top"
			/>}
		</div>
	</div>)
}

export default function Search({ ...props }) {
	const head = <Helmet>
		<title>Wand - Search</title>
	</Helmet>

	const logFeat = useLogFeatureInfo()
	const history = useHistory()
	const persisted_forecast_only = localStorage.get(ADVANCED_FILTER_STORAGE_KEY) === 'true'
	const [searchTerm, setSearchTerm] = useState({ text:'', exact:false, advanced_forecast_only:persisted_forecast_only })
	const [show_advanced_filters, set_show_advanced_filters] = useState(persisted_forecast_only)
	const [show_mobile_filter_modal, set_show_mobile_filter_modal] = useState(false)
	const [rootStyle, setRootStyle] = useState<any>({ '--ios-keyboard-offset':'0px' })

	const { loading, data, error } = useTowers({ user:true, latestData:true })
	const centroid = { lat:data?.currentLocation?.latitude, lon:data?.currentLocation?.longitude }

	const toast = new Toast(...useIonToast(), { duration:5000 })

	const {
		toggle_tower,
		can_add_tower,
		get_tower_toggle_info,
		flush_pending_changes,
		has_pending_changes,
		is_saving,
		is_free,
		is_basic,
		is_pro,
		has_team_toggled_towers
	} = useToggleTowerForecast({
		onCompleted: () => {
			toast.show('Tower changes saved.')
		},
		on_error: (err:any) => {
			console.error(err)
			toast.show('Failed to save changes. Please try again.', { error:true, closeText:'close' })
		}
	})

	// Flush pending changes when navigating away
	useEffect(() => {
		return () => {
			flush_pending_changes()
		}
	}, [flush_pending_changes])

	// Modal for quota exceeded
	const [show_quota_modal, dismiss_quota_modal] = useIonModal(QuotaExceededModal, {
		current: can_add_tower().current,
		max: can_add_tower().max,
		plan_name: can_add_tower().plan_name || 'Free',
		onDismiss: () => {
			dismiss_quota_modal()
		},
		onGoToSearch: () => {
			dismiss_quota_modal()
			// Already on Search page, just scroll to top or show message
			window.scrollTo({ top: 0, behavior: 'smooth' })
			toast.show('Disable 24H forecast on towers below to free up space.')
		},
		onGoToUpgrade: () => {
			dismiss_quota_modal()
			history.push(SETTINGS_UPGRADE_PLAN_PATH + '?plan=organization')
		}
	})

	// Modal for team-toggled tower (cannot untoggle)
	const [show_team_toggled_modal, dismiss_team_toggled_modal] = useIonModal(TeamToggledModal, {
		onDismiss: () => {
			dismiss_team_toggled_modal()
		}
	})

	// Wrapper for toggle_tower that checks quota before toggling
	const handle_toggle_tower = (tower_id: string, enable: boolean, set_switch?: (val: boolean) => void) => {
		// If trying to enable (turn ON), check quota first
		if (enable) {
			const quota_check = can_add_tower(tower_id)
			if (!quota_check.can) {
				// Don't toggle the switch, show modal instead
				if (set_switch) {
					set_switch(false)
				}
				show_quota_modal()
				return
			}
		}

		// Proceed with toggle
		const result = toggle_tower(tower_id, enable, set_switch)

		// If toggle failed due to team-toggled tower, show modal
		if (!result.success && result.is_team_toggled) {
			show_team_toggled_modal({
				cssClass: 'team-toggled-modal'
			})
		}
	}

	const get_upgrade_message = () => {
		if (is_pro || is_basic) {
			const plan_name = is_basic ? 'Solo' : 'Professional'
			return `Your current plan is '${plan_name}'. This plan can manage up to 10 towers at a time. To manage more than 10 towers at a time, please upgrade to the 'Organisation' plan.`
		}
		return `Your current plan is 'Free'. This plan can manage up to 3 towers at a time. To manage more than 3 towers at a time, please upgrade to the 'Solo', 'Professional' or 'Organisation' plan.`
	}

	const upgrade_message = get_upgrade_message()

	const [show_modal, dismiss_modal] = useIonModal(UpgradePopup, {
		history,
		message: upgrade_message,
		onCancel: () => {
			dismiss_modal()
		},
		on_upgrade:() => {
			dismiss_modal()
			history.push(SETTINGS_UPGRADE_PLAN_PATH)
		}
	})

	useEffect(() => {
		// if (OS == IOS) {
			const adjustTowerList = () => {
				if (window?.visualViewport?.height) {
					const el = document.querySelector('div.tower-items')
					const towerListPosY = el ? el.getBoundingClientRect().y : null
					if (towerListPosY !== null) {
						const towerListHeight = window?.visualViewport?.height - towerListPosY
						if (towerListHeight < 0)
							setRootStyle({ '--ios-keyboard-offset': `${230-towerListHeight}px` })
						else
							setRootStyle({ '--ios-keyboard-offset': `0px` })
					}
				}
			}

			if (window.visualViewport)
				window.visualViewport.addEventListener('resize', adjustTowerList)

			return () => {
				if (window.visualViewport)
					window.visualViewport.removeEventListener('resize', adjustTowerList)
			}
		// }
	},[])

	const authed = useAuth()
	if (authed === null)
		return null

	const onTowerClick = (tower:any) => {
		setSearchTerm({
			text: tower.name,
			exact: true,
			advanced_forecast_only: searchTerm.advanced_forecast_only
		})
	}

	const onTowerSelect = (tower:any) => {
		if (!tower?.disabled) {
			logFeat({ 
				title:'switch_tower', 
				content:tower.name
			})
			history.push(HOME_PATH, { tower })
		} else
			show_modal()
	}

	const onChange = (text:any) => {
		setSearchTerm({
			text: text||'',
			exact: false,
			advanced_forecast_only: searchTerm.advanced_forecast_only
		})
	}

	const toggle_advanced_filter = () => {
		set_show_advanced_filters(!show_advanced_filters)
	}

	const toggle_advanced_forecast_only = (checked:boolean) => {
		localStorage.set(ADVANCED_FILTER_STORAGE_KEY, checked)
		setSearchTerm({
			...searchTerm,
			advanced_forecast_only: checked
		})
	}

	// Show the advanced filter to paid plans and to invited team members (free
	// accounts that have access to team-granted towers).
	const show_advanced_filter = !is_free || has_team_toggled_towers

	const open_mobile_filter_modal = () => {
		set_show_mobile_filter_modal(true)
	}

	const close_mobile_filter_modal = () => {
		set_show_mobile_filter_modal(false)
	}


	const absent_color = get_pin_color('absent')
	const absent_dark_color = get_pin_color('absent', true)
	const present_color = get_pin_color('present')
	const present_dark_color = get_pin_color('present', true)
	const orange_color = get_pin_color('orange')
	const orange_dark_color = get_pin_color('orange', true)
	const disabled_color = get_pin_color('disabled')
	const disabled_dark_color = get_pin_color('disabled', true)

	return (
		<IonPage>
			{head}
			<Container style={rootStyle}>
				<div className="tower-list-desktop">
					<div className="search-input">
						<SearchField
							value={searchTerm}
							onChange={onChange}
							onClear={() => setSearchTerm({ ...searchTerm, text: '', exact: false })}
							onBack={() => history.push(HOME_PATH)}/>

						{show_advanced_filter && (
							<>
								<div className="advanced-filter-toggle" onClick={toggle_advanced_filter}>
									<Typography variant="body2">Advanced Filter</Typography>
									{show_advanced_filters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								</div>

								{show_advanced_filters && (
									<div className="advanced-filters">
										<FormControlLabel
											control={
												<Checkbox
													checked={searchTerm.advanced_forecast_only}
													onChange={(e) => toggle_advanced_forecast_only(e.target.checked)}
													color="primary"
												/>
											}
											label={<Typography className="filter-label">24H forecast only</Typography>}
										/>
									</div>
								)}
							</>
						)}

						<div className="legend">
							<div className="legend-item" style={{ '--pin-color':absent_color, '--pin-border-color':absent_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">Absent</div>
								</div>
							</div>
							<div className="legend-item" style={{ '--pin-color':orange_color, '--pin-border-color':orange_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">T° Inversion</div>
								</div>
							</div>
							<div className="legend-item" style={{ '--pin-color':present_color, '--pin-border-color':present_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">Present</div>
								</div>
							</div>
							<div className="legend-item" style={{ '--pin-color':disabled_color, '--pin-border-color':disabled_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">Offline</div>
								</div>
							</div>
						</div>
						{(has_pending_changes || is_saving) && (
							<div style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: '8px 12px',
								marginTop: '8px',
								backgroundColor: is_saving ? '#dbeafe' : '#fef3c7',
								borderRadius: '6px',
								gap: '8px'
							}}>
								<SyncIcon sx={{
									fontSize: '16px',
									color: is_saving ? '#1976d2' : '#f59e0b',
									animation: is_saving ? 'spin 1s linear infinite' : 'none',
									'@keyframes spin': {
										'0%': { transform: 'rotate(0deg)' },
										'100%': { transform: 'rotate(360deg)' }
									}
								}} />
								<Typography variant="body2" sx={{ color: is_saving ? '#1976d2' : '#92400e', fontSize: '12px' }}>
									{is_saving ? 'Saving changes...' : 'Changes pending...'}
								</Typography>
							</div>
						)}
					</div>
					{loading ?
					<div className="towers-loading">
						<CircularProgress/>
					</div> : error ? 
					<Alert 
						severity="error"
						sx={{ marginLeft: '8px', marginRight: '8px' }}>
						{error?.message||'Oops, something went wrong on our side!'}
					</Alert> :
					<List className="tower-items">{filterTowers(data?.towers, searchTerm).map((tower:any) => { return (
						<Fragment key={tower.id}>
							<ListItem disablePadding>
								<ListItemButton sx={{ width:'100%' }}>
									<TowerDetails
										key={`${tower.id}-${tower.twenty_four_hrs_forecast_toggled?.value}`}
										tower={tower}
										show_toggle_switch={!is_free}
										toggle_tower={handle_toggle_tower}
										get_tower_toggle_info={get_tower_toggle_info}
										on_click={() => onTowerSelect(tower)}/>
								</ListItemButton>
							</ListItem>
							<Divider/>
						</Fragment>)})}
						<div style={{ width:'100%', height:'300px' }}></div>
					</List>}
				</div>
				<div className={`tower-list-mobile${(data?.towers||[])[0]?.distance === null ? ' short-cards' : ''}`}>
					<div className="search-input">
						<SearchField
							value={searchTerm}
							onChange={onChange}
							onClear={() => setSearchTerm({ ...searchTerm, text: '', exact: false })}
							onBack={() => history.push(HOME_PATH)}
							showMenu={!is_free}
							onMenuClick={open_mobile_filter_modal}/>

						<div className="legend">
							<div className="legend-item" style={{ '--pin-color':absent_color, '--pin-border-color':absent_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">Absent</div>
								</div>
							</div>
							<div className="legend-item" style={{ '--pin-color':orange_color, '--pin-border-color':orange_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">T° Inversion</div>
								</div>
							</div>
							<div className="legend-item" style={{ '--pin-color':present_color, '--pin-border-color':present_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">Present</div>
								</div>
							</div>
							<div className="legend-item" style={{ '--pin-color':disabled_color, '--pin-border-color':disabled_dark_color }}>
								<div className="legend-container">
									<div className="swatch"></div>
									<div className="swatch-label">Offline</div>
								</div>
							</div>
						</div>
						{(has_pending_changes || is_saving) && (
							<div style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: '6px 10px',
								marginTop: '6px',
								backgroundColor: is_saving ? '#dbeafe' : '#fef3c7',
								borderRadius: '6px',
								gap: '6px'
							}}>
								<SyncIcon sx={{
									fontSize: '14px',
									color: is_saving ? '#1976d2' : '#f59e0b',
									animation: is_saving ? 'spin 1s linear infinite' : 'none',
									'@keyframes spin': {
										'0%': { transform: 'rotate(0deg)' },
										'100%': { transform: 'rotate(360deg)' }
									}
								}} />
								<Typography variant="body2" sx={{ color: is_saving ? '#1976d2' : '#92400e', fontSize: '11px' }}>
									{is_saving ? 'Saving...' : 'Pending...'}
								</Typography>
							</div>
						)}
					</div>
					<div className="tower-items">
						<div style={{ display:'flex', height:TOWER_HEIGHT_PX }}>
							<div className="tower-left-gutter"></div>{filterTowers(data?.towers, searchTerm).map((tower:any) => { return (
							<div 
								key={tower.id} 
								className={`tower-card${tower.distance === null ? ' short-card' : ''}`}>
								<TowerDetails
									key={`${tower.id}-${tower.twenty_four_hrs_forecast_toggled?.value}`}
									tower={tower}
									show_toggle_switch={!is_free}
									toggle_tower={handle_toggle_tower}
									get_tower_toggle_info={get_tower_toggle_info}
									on_click={() => onTowerSelect(tower)}/>
							</div>)})}
						</div>
					</div>
				</div>
				<div className="googlep-map">
					<GoogleMap 
						center={centroid}
						zoom={6}
						towers={data?.towers}
						onTowerClick={onTowerClick}
						currentLoc={centroid}
						/>
				</div>
			</Container>

			<FilterModal className={show_mobile_filter_modal ? 'open' : ''}>
				<div className="modal-header">
					<IconButton className="close-button" onClick={close_mobile_filter_modal}>
						<CloseIcon />
					</IconButton>
					<Typography className="modal-title">Filters</Typography>
				</div>
				<div className="modal-content">
					<div className="filter-section">
						<Typography className="section-title">Advanced Filter</Typography>
						<div className="filter-option">
							<FormControlLabel
								control={
									<Checkbox
										checked={searchTerm.advanced_forecast_only}
										onChange={(e) => toggle_advanced_forecast_only(e.target.checked)}
										color="primary"
									/>
								}
								label={<Typography className="filter-label">24H forecast only</Typography>}
							/>
						</div>
					</div>
				</div>
			</FilterModal>
		</IonPage>)
}





